import { Input } from 'cd-eft/components/common/Input';
import { observer } from 'mobx-react';
import React, { Fragment, useEffect, useRef } from 'react';
import { FormulaBanner } from '../Common/FormulaBanner';
import {
  InputContainer,
  DescriptionContainer,
  ImageContainer,
  FormulaPrintModalContainer,
} from '../template/FormulaFormTemplates';
import { SubsurfaceDrainageVM } from './SubsurfaceDrainageVM';
import { ComboBox } from 'cd-eft/components/common/ComboBox';
import { Loader } from 'cd-eft/components/common/LoadingSpinner/LoadingSpinner';
import { Table } from 'reactstrap';
import './SubsurfaceDrainage.scss';
import pdflogo from 'cd-eft/assets/images/pdf-blue.svg';


const SubsurfaceDrainageComponent = (props) => {
  const vm = useRef(new SubsurfaceDrainageVM(props)).current;
  useEffect(() => {
    // on initial render, load form data - desc, img, help link
    vm.getFormData(`${props.section}_Subsurface Inlet_${props.id}`);
  }, []);
  return (
    <Fragment>
      <FormulaBanner title={vm.formulaTitle} help={vm.bannerHelpLink} />
      <div className="row formula-content subsurface-formula-content">
        <InputContainer
          computeCallback={() => {
            vm.computeSubsurfaceDrainage();
          }}
          printCallback={() => {
            vm.launchAndExport();
          }}
          isLoading={vm.isLoading}
          disableCompute={vm.disableComputeBtn}
          disablePrint={!vm.hasResults || vm.isLoading || vm.disablePrintBtn}
        >
          <span className="input-help-header">
            <a href={vm.helpLink} target='_blank'>Help select n value <img src={pdflogo} alt={'PDF logo'} /></a>
          </span>
          <h2 className="sections-heading">General</h2>
          <div className="row">
            <div className="col-auto">
              <ComboBox
                idAndName="drainage-coefficient"
                label="Drainage Coefficient (in/day) "
                aria-label="Drainage Coefficient"
                valid={!vm.formValidation.drainageCoefficient}
                value={vm.subsurfaceDrainageFormData.drainageCoefficient}
                onChange={(e) => {
                  vm.handleInputChange(e);
                }}
                onBlur={(e) => vm.validateInput(e.target.name, parseFloat(e.target.value))}
                options={vm.subsurfaceDrainageOptions}
                errorMsg={''}
                required={true}
                className={vm.subsurfaceDrainageErrorClass('drainageCoefficient')}
              />
            </div>
            <div className="col-auto margin">
              <Input
                label="Manning's n"
                idAndName="mannings-n"
                valid={!vm.formValidation.manningsN}
                value={vm.subsurfaceDrainageFormData.manningsN}
                type="number"
                onChange={(e) => {
                  vm.handleInputChange(e);
                }}
                step={0.0001}
                onBlur={(e) => vm.validateInput(e.target.name, parseFloat(e.target.value))}
                className={vm.subsurfaceDrainageErrorClass('manningsN')}
                onKeyDown={vm.handleOnKeyDown}
                min={0.0001}
              />
            </div>
          </div>

          <div className="table-container">
            <Table className="usa-table subsurface">
              <thead>
                <tr>
                  <th colSpan={2} className="input-header sections-heading">
                    Input
                  </th>
                  <th colSpan={5} className="results-header sections-heading">
                    Results
                  </th>
                </tr>
                <tr>
                  <th className="input-subheader input-first-cell">Slope (ft/ft)</th>
                  <th className="input-subheader input-second-cell">Acres</th>
                    <>
                      <th className="results-header results-header-border">Flow Q (cfs)</th>
                      <th className="results-header results-header-border">Capacity (cfs)</th>
                      <th className="results-header results-header-border">Max Acres</th>
                      <th className="results-header results-header-border">Diameter (in)</th>
                      <th className="results-header results-header-border">Velocity (fps)</th>
                    </>
                </tr>
              </thead>
              <tbody>
                {vm.subsurfaceDrainageFormData.slopeList.length > 0
                  ? vm.subsurfaceDrainageFormData.slopeList.map((item, index) => (
                      <tr key={'subsurface-' + index}>
                        <td className="">
                          <input
                            aria-label={`table-inputs-slope-${index}`}
                            id={`table-inputs-slope-${index}`}
                            name={`table-inputs-slope-${index}`}
                            className="table-inputs"
                            type="number"
                            onBlur={(e) => vm.validateInput(e.target.name, parseFloat(e.target.value), index)}
                            value={(index < vm.subsurfaceDrainageFormData.slopeList.length && vm.subsurfaceDrainageFormData.slopeList[index] !==0 ) ? (vm.subsurfaceDrainageFormData.slopeList[index]): '0.0000'}
                            onChange={(e) => vm.handleInputChange(e, index)}
                            min={0.0001}
                            onKeyDown={vm.handleOnKeyDown}
                            step={0.0001}
                          />
                        </td>
                        <td className="">
                          <input
                            aria-label = {`table-inputs-area-${index}`}
                            id={`table-inputs-area-${index}`}
                            name={`table-inputs-area-${index}`}
                            className="table-inputs"
                            type="number"
                            onBlur={(e) => vm.validateInput(e.target.name, parseFloat(e.target.value), index)}
                            value={(index < vm.subsurfaceDrainageFormData.areaList.length && vm.subsurfaceDrainageFormData.areaList[index] !==0 ) ? (vm.subsurfaceDrainageFormData.areaList[index]): '0.00'}
                            onChange={(e) => vm.handleInputChange(e,index)}
                            min={0.01}
                            step={0.01}
                            onKeyDown={vm.handleOnKeyDown}
                          />
                        </td>
                        {vm.resultsRows ?
                        <>
                          <td className="results-header">{index < vm.resultsRows.length ? vm.roundingHelperString(parseFloat(vm.resultsRows[index]?.qreq), 2) : ''}</td>
                          <td className="results-header">{index < vm.resultsRows.length ? vm.roundingHelperString(parseFloat(vm.resultsRows[index]?.qavail), 2) : ''}</td>
                          <td className="results-header">{index < vm.resultsRows.length ? vm.roundingHelperString(parseFloat(vm.resultsRows[index]?.acresAvail), 2) : ''}</td>
                          <td className="results-header">{index < vm.resultsRows.length ? vm.roundingHelperString(parseFloat(vm.resultsRows[index]?.diameter), 2) : ''}</td>
                          <td className="results-header">{index < vm.resultsRows.length ? vm.roundingHelperString(parseFloat(vm.resultsRows[index]?.velocity), 2) : ''}</td>
                        </> : null}
                      </tr>
                    ))
                  : null}
              </tbody>
            </Table>
          </div>
        </InputContainer>

        <div className="col formula-information-surface-inlet">
          <div className="row formula-description">
            <DescriptionContainer>
              {vm.isLoading && <Loader message={''} />}
              <p>{vm.formDescription}
              &nbsp;
                <a target="_blank" href={vm.bannerHelpLink}>
                  Learn More
                  <img src={pdflogo} alt={'PDF logo'} />
                </a>
                </p>
            </DescriptionContainer>
          </div>
          <div className="row formula-images">
            <ImageContainer>
              {vm.image ? (
                <img
                  className="Subsurface Drainage-img"
                  src={vm.image}
                  alt={vm.hydraulicFormulaImageDescription}
                  title={vm.formulaTitle}
                />
              ) : null}
            </ImageContainer>
          </div>
        </div>
      </div>
      <FormulaPrintModalContainer
        vm={vm}
        inputs={vm.exportedInputs}
        results={vm.exportedResults}
        images={[vm.image]}
        formulaName={vm.formulaTitle}
        tableData={true}
      />
    </Fragment>
  );
};

export const SubsurfaceDrainageFormula = observer(SubsurfaceDrainageComponent);
